import {SoundLoader} from "../loading/SoundLoader";
import {LoaderEvent} from "../events/LoaderEvent";
import {EventDispatcher} from "../events/EventDispatcher";
import {Device} from "../Device";
import {Event} from "../events/Event";
import * as log from "../utils/log";
import {Sound} from "./Sound"

@log.decoratorConfig({style: 'color: #A3D9FF'})
export class SoundManager extends EventDispatcher {

    log;
    warn;
    error;

    static SOUND_LOADED: string = "soundLoaded";

    protected _soundLoaders: any = {};
    protected _cacheTimestamp: string;
    protected _audioContext: AudioContext;
    protected _masterGain: GainNode;
    protected _soundSources: any = {};
    protected _sounds: any = {};
    protected _soundBuffers: any = {};
    protected _mute: boolean = true;

    constructor(cacheTimestamp?: string) {
        super();

        this._cacheTimestamp = cacheTimestamp;

        if (Device.isSoundSupported()) {
            // there is a limit of AudioContext instance that can exist
            // so make sure to use one instance
            // TODO: this class should be moved in the library in package sound
            // and audioContextInstance should not be global
            if ((window as any).audioContextInstance == null)
                (window as any).audioContextInstance = new (Device.getAudioContext())();
            this._audioContext = (window as any).audioContextInstance;
            // TODO: test volume                 on all devices
            //                if(!this._audioContext.                createGainNode)
            //                    this._audioContext.createGainNode = this._audioContext.createGain;

            this._masterGain = this._audioContext.createGain();
            this._masterGain.connect(this._audioContext.destination);
            this._masterGain.gain.value = 1;

            window.addEventListener("focus", ()=>{
                if((window as any).audioContextInstance.state !== 'running'){
                    this.dispatchEvent(new Event("soundInterruptedFromBrowser"));
                    this.isMuted = true;
                }
            })
        }
    }

    playDummySound(dummyBuffer: AudioBuffer) {
        if (this._audioContext) {
            var source = this._audioContext.createBufferSource();
            source.buffer = dummyBuffer;
            source.connect(this._audioContext.destination);
            source.start(0, 0, 1);
        }
    }

    registerSounds(sounds: Array<any>, prependURL?: string, cacheTimestamp?: string) {
        if (sounds.length == 0)
            return;

        const len = sounds.length;
        for (var i = 0; i < len; i++) {
            const id: string = sounds[i].id || sounds[i].src;

            // don`t override already existing sounds
            if (this._soundSources[id]) {
                this.warn('Sound[%s] already registered', sounds[i].src);
                continue;
            }

            this._soundSources[id] = sounds[i].src;
            var soundGroup: Array<any> = sounds[i].sounds;
            const len2 = soundGroup.length;
            for (var j = 0; j < len2; j++) {
                var obj = soundGroup[j];
                this._sounds[obj.name] = {start: obj.start, duration: obj.duration, soundId: id, name: obj.name};
            }

            this.log('Loading sound[%s]...', sounds[i].src);

            const soundLoader = new SoundLoader(this._soundSources[id], cacheTimestamp || this._cacheTimestamp, prependURL, id);
            soundLoader.addEventListener(LoaderEvent.COMPLETE, this.onSoundLoaded, this);
            soundLoader.addEventListener(LoaderEvent.ERROR, this.onSoundError, this);
            this._soundLoaders[id] = soundLoader;
        }
    }

    hasSound(soundName: string) {
        if (!this._sounds[soundName])
            return false;

        return true;
    }

    isSoundLoaded(soundName: string) {
        // TODO: check the second operand
        if (!this._sounds[soundName] || !this._soundBuffers[this._sounds[soundName].soundId])
            return false;

        return true;
    }

    load(id: string) {
        if (!this._audioContext || this._soundBuffers[id] !== undefined)
            return;

        this._soundBuffers[id] = null;

        this._soundLoaders[id].load();
    }

    loadAll() {
        for (var key in this._soundSources) {
            this.load(key);
        }
    }

    getSoundDuration(soundName: string): number {
        const soundInfo = this._sounds[soundName];

        if (!soundName || !this._audioContext || !soundInfo ||
            !this._soundBuffers[soundInfo.soundId])
            return -1;

        return soundInfo.duration;
    }

    play(soundName: string, loop: boolean = false, volume: number = 1): Sound {
        if (!soundName || !this._audioContext || !this._sounds[soundName] ||
            !this._soundBuffers[this._sounds[soundName].soundId]) {
            this.warn('Unable to play sound[%s]', soundName);
            return null;
        }

        if (this._audioContext.state != "running") {
            this._audioContext.resume();

            if (!loop) {
                return;
            }
        }

        var soundInfo = this._sounds[soundName];

        var source: AudioBufferSourceNode = this._audioContext.createBufferSource();
        source.buffer = this._soundBuffers[soundInfo.soundId];

        const instance = new SoundInstance(source, this._masterGain, soundInfo, loop);
        instance.volume = volume;

        return instance;
    }

    playStream(soundURI: string): Sound {


        if (!soundURI || !this._audioContext) {
            this.warn('Unable to stream sound[%s]', soundURI);
            return null;
        }

        const audioElement: HTMLAudioElement = document.createElement("audio");
        audioElement.crossOrigin = "anonymous";

        const source: MediaElementAudioSourceNode = this._audioContext.createMediaElementSource(audioElement);

        audioElement.muted = this.isMuted;

        source.connect(this._audioContext.destination);

        audioElement.src = soundURI;

        audioElement.autoplay = true;

        audioElement.addEventListener("ended", () => {

            source.disconnect(this._audioContext.destination);

        });

        return new StreamSoundInstance(source, this._masterGain, audioElement);
    }


    get isContextSuspended(): boolean {
        return this._audioContext && this._audioContext.state == "suspended" ? true : false;
    }

    get isMuted(): boolean {
        return this._mute;
    }

    set isMuted(value: boolean) {
        if (!this._audioContext)
            return;

        this._mute = value;

        if (this._mute)
            this._masterGain.gain.value = 0;
        else
            this._masterGain.gain.value = 1;
    }

    dispose() {
        for (let key in this._soundLoaders) {
            const soundLoader: SoundLoader = this._soundLoaders[key];
            soundLoader.removeEventListener(LoaderEvent.COMPLETE, this.onSoundLoaded, this);
            soundLoader.removeEventListener(LoaderEvent.ERROR, this.onSoundError, this);
            soundLoader.dispose();
        }

        super.dispose();
    }

    protected onSoundLoaded(event: LoaderEvent) {
        event.target.removeEventListener(LoaderEvent.COMPLETE, this.onSoundLoaded, this);
        event.target.removeEventListener(LoaderEvent.ERROR, this.onSoundError, this);

        const id = event.target.id;

        this.log('Sound loaded[%s]', this._soundSources[id]);

        this._soundBuffers[id] = event.data;

        this.dispatchEvent(new Event(SoundManager.SOUND_LOADED, {soundId: id}));
    }

    protected onSoundError(event: LoaderEvent) {
        this.error('Unable to load sound[%s]', event.target.src);
        event.target.removeEventListener(LoaderEvent.COMPLETE, this.onSoundLoaded, this);
        event.target.removeEventListener(LoaderEvent.ERROR, this.onSoundError, this);
    }
}

class StreamSoundInstance extends EventDispatcher implements Sound {

    playbackRate: number;

    protected _audioElement: HTMLAudioElement;
    protected _source: MediaElementAudioSourceNode;
    protected _gainNode: GainNode;
    protected _hasSoundEned: boolean = false;

    constructor(source: MediaElementAudioSourceNode, masterGain: GainNode, audioElement: HTMLAudioElement) {

        super();

        this._gainNode = source.context.createGain();
        this._gainNode.connect(masterGain);
        source.connect(this._gainNode);
        this._source = source;

        this._audioElement = audioElement;
    }

    get volume(): number {
        return this._gainNode.gain.value;
    }

    set volume(value: number) {

        value = Math.min(Math.max(value, 0), 1);

        this._gainNode.gain.value = value;
        this._audioElement.volume = value;
    }

    stop(): void {
        this._audioElement.pause();
    }

    protected onSoundEnded() {
        this.dispatchEvent(new Event("ended"));
    }

}

@log.decoratorConfig({style: 'color: #006E90'})
class SoundInstance extends EventDispatcher implements Sound {
    log;
    protected _source: AudioBufferSourceNode;
    protected _gainNode: GainNode;
    protected _soundInfo: any;
    protected _isStopped: boolean;
    protected _hasSoundEnded: boolean = false;
    protected _onEndDelay: gsap.TweenMax;

    constructor(source: AudioBufferSourceNode, masterGain: GainNode,
                soundInfo: any, loop: boolean) {
        super();

        this._gainNode = source.context.createGain();
        this._gainNode.connect(masterGain);
        source.connect(this._gainNode);
        source.loop = loop;
        source.loopStart = soundInfo.start;
        source.loopEnd = soundInfo.start + soundInfo.duration;

        source.onended = this.onSoundEnded.bind(this);

        this._source = source;
        this._soundInfo = soundInfo;

        this.log('Started');

        if (loop) {
            this._source.start(0, this._soundInfo.start);
        } else {
            this._onEndDelay = gsap.TweenMax.delayedCall(soundInfo.duration, this.onSoundEnded, null, this);
            this._source.start(0, this._soundInfo.start, this._soundInfo.duration);
        }
    }

    get volume(): number {
        return this._gainNode.gain.value;
    }

    set volume(value: number) {
        value = Math.min(Math.max(value, 0), 1);
        this._gainNode.gain.value = value;
    }

    get playbackRate(): number {
        return this._source.playbackRate.value;
    }

    // TODO: test if this value is readonly
    set playbackRate(value: number) {
        value = Math.abs(value);
        this._source.playbackRate.value = value;
    }

    stop(suppressEndedEvent: boolean = false): void {
        if (!this._isStopped) {
            this.log('Stopped');
            this._isStopped = true;
            this._hasSoundEnded = suppressEndedEvent;
            this._source.stop();

            if (this._onEndDelay != null) {
                this._onEndDelay.totalTime(this._onEndDelay.totalDuration());
            }
        }
    }

    protected onSoundEnded() {
        if (this._onEndDelay) {
            this._onEndDelay.kill();
            this._onEndDelay = null;
        }

        if (!this._hasSoundEnded) {
            this.log('Ended');

            this._hasSoundEnded = true;
            this.dispatchEvent(new Event("ended"));
        }
    }

    toString() {
        return `${this.constructor['name']} ${this._soundInfo.soundId}/${this._soundInfo.name}`;
    }
}
